'use client';

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export default function Modal({ isOpen, onClose, title, children, size = 'lg' }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  return createPortal(
    <div data-component-id="src/components/ui/Modal.tsx:51:4" data-component-path="src/components/ui/Modal.tsx" data-component-line="51" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-50%20flex%20items-center%20justify-center%20p-4%22%7D" className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div data-component-id="src/components/ui/Modal.tsx:53:6" data-component-path="src/components/ui/Modal.tsx" data-component-line="53" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-black%2F50%20backdrop-blur-sm%20transition-opacity%22%7D" 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div data-component-id="src/components/ui/Modal.tsx:59:6" data-component-path="src/components/ui/Modal.tsx" data-component-line="59" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" 
        ref={modalRef}
        className={`relative bg-white rounded-2xl shadow-2xl w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col animate-in fade-in-0 zoom-in-95 duration-300`}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div data-component-id="src/components/ui/Modal.tsx:68:8" data-component-path="src/components/ui/Modal.tsx" data-component-line="68" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20p-6%20border-b%20border-gray-200%22%7D" className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 data-component-id="src/components/ui/Modal.tsx:69:10" data-component-path="src/components/ui/Modal.tsx" data-component-line="69" data-component-file="Modal.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22id%22%3A%22modal-title%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-gray-900%22%7D" id="modal-title" className="text-xl font-semibold text-gray-900">
            {title}
          </h2>
          <button data-component-id="src/components/ui/Modal.tsx:72:10" data-component-path="src/components/ui/Modal.tsx" data-component-line="72" data-component-file="Modal.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-2%20text-gray-400%20hover%3Atext-gray-600%20hover%3Abg-gray-100%20rounded-lg%20transition-colors%22%7D"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <svg data-component-id="src/components/ui/Modal.tsx:77:12" data-component-path="src/components/ui/Modal.tsx" data-component-line="77" data-component-file="Modal.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path data-component-id="src/components/ui/Modal.tsx:78:14" data-component-path="src/components/ui/Modal.tsx" data-component-line="78" data-component-file="Modal.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div data-component-id="src/components/ui/Modal.tsx:84:8" data-component-path="src/components/ui/Modal.tsx" data-component-line="84" data-component-file="Modal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20p-6%22%7D" className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
